import { useCallback, useRef } from "react";
import { findBestPowerCable } from "../utils/cableMatcher";
import { findBestSignalCable } from "../utils/signalCableMatcher";
import { lookupConnectorCompat } from "../utils/signalCompatibility";
import { findAutoGroupRigCable } from "../utils/powerCompatibility";
import { isDirectAttachConnection } from "../utils/directAttach";

function autoFillBlankBoundary(boundaryNode, deviceNode, handleId, updateBoundary) {
  const devicePorts = deviceNode.data?.device?.device_ports || [];
  const port = devicePorts.find((p) => String(p.id) === handleId);
  if (!port) return;

  const b = boundaryNode.data.boundary;
  const isBlank = !b.connector && !b.signal_type && !b.signal_level && !b.phantom_power;
  if (!isBlank) return;

  const isInput = b.direction === "input";
  let connector = port.connector || null;
  let signalLevel = port.signal_level || null;
  if (isInput) {
    if (connector === "xlr_combo") connector = "xlr";
    if (signalLevel === "mic_line") signalLevel = "mic";
  }
  updateBoundary(boundaryNode.id, {
    connector,
    signal_type: port.signal_type || null,
    signal_level: signalLevel,
    phantom_power: port.phantom_power || null,
  });
}

export default function useConnections({
  nodes,
  edges,
  rigCables,
  addConnection,
  updateBoundary,
  reconnectConnection,
  cables,
  signalCables,
  trackEvent,
}) {
  const connectStartRef = useRef(null);

  const onConnect = useCallback(
    (params) => {
      connectStartRef.current = null;

      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);
      if (!sourceNode || !targetNode) return;

      // Prevent duplicate signal connections to the same input port
      const isPowerHandle = params.sourceHandle?.startsWith("power-out-");
      if (!isPowerHandle && params.targetHandle) {
        const existingEdge = edges.find(
          (e) => e.target === params.target && e.targetHandle === params.targetHandle
        );
        if (existingEdge) return;
      }

      const isBoundaryConnection =
        sourceNode.type === "signalBoundary" || targetNode.type === "signalBoundary";

      if (isBoundaryConnection) {
        const sourceIsBoundary = sourceNode.type === "signalBoundary";
        const boundaryNode = sourceIsBoundary ? sourceNode : targetNode;
        const deviceNode = sourceIsBoundary ? targetNode : sourceNode;
        const handleId = sourceIsBoundary ? params.targetHandle : params.sourceHandle;
        autoFillBlankBoundary(boundaryNode, deviceNode, handleId, updateBoundary);
      }

      const isPower = params.sourceHandle?.startsWith("power-out-");
      const connectionType = isPower ? "power" : "signal";
      const edgeData = { connectionType };

      // Detect attached cables (hardwired into the device)
      if (isPower && !isBoundaryConnection) {
        const toDevice = targetNode.data.device;
        if (toDevice?.attached_power_cable) {
          edgeData.attachedCable = true;
        }
      }
      if (!isPower && !isBoundaryConnection) {
        const sourcePorts = sourceNode.data.device?.device_ports || [];
        const fromPort = sourcePorts.find((p) => String(p.id) === params.sourceHandle);
        if (fromPort?.attached_cable) {
          edgeData.attachedCable = true;
        }
      }

      // Detect direct-attach signal connections (mic mute, headphone splitter, etc.)
      if (!isPower && !isBoundaryConnection && !edgeData.attachedCable) {
        const sourcePorts = sourceNode.data.device?.device_ports || [];
        const targetPorts = targetNode.data.device?.device_ports || [];
        const fromPort = sourcePorts.find((p) => String(p.id) === params.sourceHandle);
        const toPort = targetPorts.find((p) => String(p.id) === params.targetHandle);
        if (isDirectAttachConnection(fromPort, toPort)) {
          edgeData.directAttach = true;
        }
      }

      // Detect supplied cables and auto-match power cables.
      let pickedCable = null;

      if (isPower && !isBoundaryConnection) {
        const toDevice = targetNode.data.device;
        const suppliedPorts = toDevice?.supplied_power_source_ports || [];
        const outputId = Number(params.sourceHandle.replace("power-out-", ""));
        const outputs = sourceNode.data.device?.power_outputs || [];
        const output = outputs.find((po) => po.id === outputId);

        if (suppliedPorts.length > 0 && output &&
            suppliedPorts.some((sp) => lookupConnectorCompat(output.connector, sp).level === "ok")) {
          edgeData.suppliedCable = true;
        }

        if (!edgeData.suppliedCable) {
          pickedCable = findBestPowerCable(cables, {
            sourceConnector: output?.connector,
            targetConnector: toDevice?.power_connector,
            targetPolarity: toDevice?.power_polarity,
            sourceVoltageOptions: output?.voltage_options,
            targetVoltageMin: toDevice?.power_voltage_min,
            targetVoltageMax: toDevice?.power_voltage_max,
            sourceEndType: output?.end_type,
            targetEndType: "jack",
            sourceCurrentMa: output?.current_ma,
            targetCurrentMa: toDevice?.power_current_ma,
          });
        }
      }

      // Auto-match signal cables
      if (!isPower && !isBoundaryConnection && !edgeData.attachedCable && !edgeData.directAttach) {
        const sourcePorts = sourceNode.data.device?.device_ports || [];
        const targetPorts = targetNode.data.device?.device_ports || [];
        const fromPort = sourcePorts.find((p) => String(p.id) === params.sourceHandle);
        const toPort = targetPorts.find((p) => String(p.id) === params.targetHandle);
        if (fromPort && toPort && fromPort.connector !== "wireless") {
          pickedCable = findBestSignalCable(signalCables || [], { fromPort, toPort });
        }
      }

      // Wrap auto-picked cable in a rig_cable so it persists across save/load.
      let autoRigCable = null;
      if (pickedCable && !edgeData.suppliedCable && !edgeData.attachedCable) {
        const tempId = `rc-${crypto.randomUUID()}`;
        autoRigCable = { tempId, cableId: pickedCable.id, cable: pickedCable, label: null };
        edgeData.cableChain = [{ rigCableTempId: tempId, cableId: pickedCable.id, cable: pickedCable }];
      }

      const edge = {
        id: `e-${params.source}-${params.sourceHandle}-${params.target}-${params.targetHandle}`,
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        data: edgeData,
      };

      // Auto-group: link to existing rig_cable if one has available plugs
      if (isPower) {
        const autoGroupRc = findAutoGroupRigCable(edge, edges, rigCables);
        if (autoGroupRc) {
          const chainEntry = { cableId: autoGroupRc.cable.id, cable: autoGroupRc.cable };
          if (autoGroupRc.id) chainEntry.rigCableId = autoGroupRc.id;
          if (autoGroupRc.tempId) chainEntry.rigCableTempId = autoGroupRc.tempId;
          edgeData.cableChain = [chainEntry];
          autoRigCable = null; // grouped under existing rig_cable, don't create new one
        }
      }

      addConnection(edge, autoRigCable);
      trackEvent("connection_made", { connectionType });
    },
    [nodes, edges, rigCables, addConnection, updateBoundary, cables, signalCables, trackEvent]
  );

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      // Prevent reconnecting to an already-occupied signal input
      const isOldPower = oldEdge.data?.connectionType === "power";
      if (!isOldPower && newConnection.targetHandle) {
        const existingEdge = edges.find(
          (e) => e.id !== oldEdge.id && e.target === newConnection.target && e.targetHandle === newConnection.targetHandle
        );
        if (existingEdge) return;
      }

      const newEdge = {
        id: `e-${newConnection.source}-${newConnection.sourceHandle}-${newConnection.target}-${newConnection.targetHandle}`,
        source: newConnection.source,
        target: newConnection.target,
        sourceHandle: newConnection.sourceHandle,
        targetHandle: newConnection.targetHandle,
        data: oldEdge.data,
      };

      const sourceNode = nodes.find((n) => n.id === newConnection.source);
      const targetNode = nodes.find((n) => n.id === newConnection.target);
      if (sourceNode && targetNode) {
        const srcIsBoundary = sourceNode.type === "signalBoundary";
        const tgtIsBoundary = targetNode.type === "signalBoundary";
        if (srcIsBoundary || tgtIsBoundary) {
          const boundaryNode = srcIsBoundary ? sourceNode : targetNode;
          const deviceNode = srcIsBoundary ? targetNode : sourceNode;
          const handleId = srcIsBoundary ? newConnection.targetHandle : newConnection.sourceHandle;
          autoFillBlankBoundary(boundaryNode, deviceNode, handleId, updateBoundary);
        }
      }

      reconnectConnection(oldEdge.id, newEdge);
      trackEvent("connection_reconnected", {});
    },
    [nodes, edges, reconnectConnection, updateBoundary, trackEvent]
  );

  const onConnectStart = useCallback(
    (_event, params) => {
      connectStartRef.current = params;
    },
    []
  );

  const onConnectEnd = useCallback(() => {
    // No dangling connections — just clear the ref
    connectStartRef.current = null;
  }, []);

  return { onConnect, onReconnect, onConnectStart, onConnectEnd };
}
